import { avatarColorFor, type AvatarPalette } from '../../../lib/avatarColor';

/** Theme tokens aligned with Inventory / Stock Movement */
export function getRequestTheme(isDark: boolean) {
  return {
    pageBg: isDark ? '#171a1f' : '#f3f4f6',
    cardBg: isDark ? '#20242b' : '#ffffff',
    barBg: isDark ? '#20242b' : '#f0f1f3',
    border: isDark ? '#343a43' : '#e5e7eb',
    headerBg: isDark ? '#20242b' : '#f0f1f3',
    inputBg: isDark ? '#171a1f' : '#f9fafb',
    text: isDark ? '#e5e7eb' : '#111827',
    muted: isDark ? '#9ca3af' : '#6b7280',
    rowHover: isDark ? '#262b35' : '#f9fafb',
    /** Expanded line-items table — inset from main request rows */
    subTableBg: isDark ? '#171a1f' : '#f0f1f3',
    subTableHeaderBg: isDark ? '#1c2129' : '#e5e7eb',
    subTableRowHover: isDark ? '#222830' : '#e8eaed',
    activeTabBg: isDark ? '#171a1f' : '#ffffff',
    activeTabTxt: isDark ? '#ffffff' : '#111827',
    inactiveTabBg: isDark ? '#2b3140' : '#e5e7eb',
    inactiveTabTxt: isDark ? '#e5e7eb' : '#374151',
  };
}

export const statusBadge = (status: string, isDark: boolean): string => {
  switch (status) {
    case 'draft':
      return isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600';
    case 'submitted':
    case 'pending_resolution':
      return isDark ? 'bg-yellow-900/40 text-yellow-400' : 'bg-yellow-100 text-yellow-700';
    case 'approved':
      return isDark ? 'bg-sky-950/50 text-sky-200' : 'bg-sky-100 text-sky-700';
    case 'partially_ordered':
      return isDark ? 'bg-violet-900/40 text-violet-300' : 'bg-violet-100 text-violet-700';
    case 'substitute':
      return isDark ? 'bg-indigo-900/40 text-indigo-300' : 'bg-indigo-100 text-indigo-700';
    case 'ordered':
      return isDark ? 'bg-indigo-900/40 text-indigo-300' : 'bg-indigo-100 text-indigo-700';
    case 'partially_received':
      return isDark ? 'bg-amber-900/40 text-amber-400' : 'bg-amber-100 text-amber-700';
    case 'received':
    case 'completed':
      return isDark ? 'bg-green-900/40 text-green-400' : 'bg-green-100 text-green-700';
    case 'cancelled':
    case 'unavailable':
      return isDark ? 'bg-red-900/40 text-red-400' : 'bg-red-100 text-red-700';
    case 'backordered':
      return isDark ? 'bg-orange-900/40 text-orange-400' : 'bg-orange-100 text-orange-700';
    case 'disputed':
      return isDark ? 'bg-rose-900/40 text-rose-300' : 'bg-rose-100 text-rose-700';
    default:
      return isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600';
  }
};

export const fmtStatusLabel = (status: string): string => {
  if (status === 'submitted') return 'Awaiting Review';
  if (status === 'approved') return 'Awaiting Order';
  return status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

/** Request header status — distinct from line-level labels */
export const fmtRequestStatusLabel = (status: string): string => {
  if (status === 'submitted') return 'Awaiting Review';
  if (status === 'approved') return 'Awaiting Order';
  if (status === 'partially_ordered') return 'Partially ordered';
  if (status === 'ordered') return 'Ordered';
  return fmtStatusLabel(status);
};

/** Shared badge sizing for request tables — fixed height keeps rows compact */
export const requestStatusBadgeClass =
  'inline-flex min-w-[7rem] h-5 max-h-5 justify-center items-center px-2 rounded text-xs font-medium whitespace-nowrap leading-none box-border';

/** Inline line-item badges — same height as status column badge */
export const lineStatusBadgeClass =
  'inline-flex h-5 max-h-5 items-center px-2 rounded text-xs font-medium whitespace-nowrap leading-none box-border flex-shrink-0';

/** Clickable workflow action badges (Approve / Ordered / Receive / Receive Issue) */
export const actionBadgeClass =
  'inline-flex items-center justify-center gap-1 h-7 px-3 rounded text-[13px] font-medium whitespace-nowrap leading-none box-border border-0 cursor-pointer transition-colors disabled:opacity-40 disabled:cursor-not-allowed';

export const actionBadge = (
  action: 'approve' | 'ordered' | 'receive' | 'issue',
  isDark: boolean,
  opts: { active?: boolean; interactive?: boolean } = {},
): string => {
  const { active = false, interactive = true } = opts;
  const hover = interactive && !active;
  switch (action) {
    case 'approve':
      if (active) return isDark ? 'bg-sky-800/70 text-sky-100' : 'bg-sky-200 text-sky-800';
      return isDark
        ? `bg-sky-900/40 text-sky-300${hover ? ' hover:bg-sky-800/60' : ''}`
        : `bg-sky-100 text-sky-700${hover ? ' hover:bg-sky-200' : ''}`;
    case 'ordered':
      if (active) return isDark ? 'bg-indigo-800/70 text-indigo-100' : 'bg-indigo-200 text-indigo-800';
      return isDark
        ? `bg-indigo-900/40 text-indigo-300${hover ? ' hover:bg-indigo-800/60' : ''}`
        : `bg-indigo-100 text-indigo-700${hover ? ' hover:bg-indigo-200' : ''}`;
    case 'receive':
      if (active) return isDark ? 'bg-green-800/70 text-green-100' : 'bg-green-200 text-green-800';
      return isDark
        ? `bg-green-900/40 text-green-400${hover ? ' hover:bg-green-800/60' : ''}`
        : `bg-green-100 text-green-700${hover ? ' hover:bg-green-200' : ''}`;
    case 'issue':
      if (active) return isDark ? 'bg-amber-800/70 text-amber-100' : 'bg-amber-200 text-amber-900';
      return isDark
        ? `bg-amber-900/40 text-amber-400${hover ? ' hover:bg-amber-800/60' : ''}`
        : `bg-amber-100 text-amber-700${hover ? ' hover:bg-amber-200' : ''}`;
    default:
      return isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600';
  }
};

/** Disposition radio buttons in line options panel */
export const dispositionRadioClass = 'request-disposition-radio flex-shrink-0';

/** Shared text/select field below disposition radios — fixed height avoids layout shift */
export const dispositionFieldClass =
  'request-disposition-field text-xs px-2 rounded border w-full box-border focus:outline-none focus:ring-1 focus:ring-blue-500';

/** User badge — same height as status badges */
export const userBadgeClass =
  'inline-flex max-w-full h-5 max-h-5 items-center px-2 rounded text-xs font-medium truncate leading-none box-border';

export function userBadgeStyle(userId: string, isDark: boolean): { backgroundColor: string; color: string } {
  const colors = avatarColorFor(userId, isDark);
  return { backgroundColor: colors.bg, color: colors.text };
}

/** Supplier name badge — pill, fixed height so order rows stay 2rem */
export const supplierBadgeClass =
  'inline-flex items-center justify-center px-2 rounded-full text-[11px] font-medium whitespace-nowrap leading-none box-border flex-shrink-0 max-w-full truncate overflow-hidden';

/** Wider palette than avatars so nearby supplier names look more distinct */
const SUPPLIER_COLOR_PALETTE: AvatarPalette[] = [
  { dark: { bg: 'rgba(59,130,246,0.35)', text: '#93c5fd' }, light: { bg: '#dbeafe', text: '#1d4ed8' } },
  { dark: { bg: 'rgba(16,185,129,0.35)', text: '#6ee7b7' }, light: { bg: '#d1fae5', text: '#047857' } },
  { dark: { bg: 'rgba(239,68,68,0.35)', text: '#fca5a5' }, light: { bg: '#fee2e2', text: '#b91c1c' } },
  { dark: { bg: 'rgba(245,158,11,0.35)', text: '#fcd34d' }, light: { bg: '#fef3c7', text: '#b45309' } },
  { dark: { bg: 'rgba(168,85,247,0.35)', text: '#d8b4fe' }, light: { bg: '#ede9fe', text: '#6d28d9' } },
  { dark: { bg: 'rgba(6,182,212,0.35)', text: '#67e8f9' }, light: { bg: '#cffafe', text: '#0e7490' } },
  { dark: { bg: 'rgba(236,72,153,0.35)', text: '#f9a8d4' }, light: { bg: '#fce7f3', text: '#be185d' } },
  { dark: { bg: 'rgba(132,204,22,0.35)', text: '#bef264' }, light: { bg: '#ecfccb', text: '#4d7c0f' } },
  { dark: { bg: 'rgba(249,115,22,0.35)', text: '#fdba74' }, light: { bg: '#ffedd5', text: '#c2410c' } },
  { dark: { bg: 'rgba(99,102,241,0.35)', text: '#a5b4fc' }, light: { bg: '#e0e7ff', text: '#4338ca' } },
  { dark: { bg: 'rgba(20,184,166,0.35)', text: '#5eead4' }, light: { bg: '#ccfbf1', text: '#0f766e' } },
  { dark: { bg: 'rgba(244,63,94,0.35)', text: '#fda4af' }, light: { bg: '#ffe4e6', text: '#be123c' } },
  { dark: { bg: 'rgba(14,165,233,0.35)', text: '#7dd3fc' }, light: { bg: '#e0f2fe', text: '#0369a1' } },
  { dark: { bg: 'rgba(217,70,239,0.35)', text: '#f0abfc' }, light: { bg: '#fae8ff', text: '#a21caf' } },
  { dark: { bg: 'rgba(234,179,8,0.35)', text: '#fde047' }, light: { bg: '#fef9c3', text: '#a16207' } },
  { dark: { bg: 'rgba(34,197,94,0.35)', text: '#86efac' }, light: { bg: '#dcfce7', text: '#15803d' } },
];

export function supplierBadgeStyle(
  name: string,
  isDark: boolean,
): { backgroundColor: string; color: string; height: number; maxHeight: number; minHeight: number; lineHeight: string } {
  const key = name.trim().toLowerCase();
  let h = 0;
  for (let i = 0; i < key.length; i++) {
    h = ((h << 5) - h + key.charCodeAt(i)) | 0;
  }
  const palette = SUPPLIER_COLOR_PALETTE[Math.abs(h) % SUPPLIER_COLOR_PALETTE.length];
  const colors = isDark ? palette.dark : palette.light;
  return {
    backgroundColor: colors.bg,
    color: colors.text,
    height: 16,
    maxHeight: 16,
    minHeight: 16,
    lineHeight: '16px',
  };
}
